import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ShieldCheck, AlertTriangle, FileText, ArrowRight, Send, Scale, FileStack, Hourglass, DollarSign, ShieldX, GitFork, CheckCircle } from 'lucide-react';

const PermitFeasibility = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    projectType: '',
    facilityType: '',
    projectLocation: '',
    projectStage: '',
    estimatedCapacity: '',
    fuelTypes: '',
    constructionTimeline: '',
    budgetRange: '',
    specificRequirements: '',
    additionalInfo: ''
  });

  const projectTypes = [
    { value: '', label: 'Select project type' },
    { value: 'new-facility', label: 'New Facility Construction' },
    { value: 'facility-expansion', label: 'Facility Expansion' },
    { value: 'equipment-upgrade', label: 'Equipment Upgrade/Replacement' },
    { value: 'process-modification', label: 'Process Modification' },
    { value: 'temporary-operation', label: 'Temporary Operation' },
    { value: 'pilot-project', label: 'Pilot/Test Project' }
  ];

  const facilityTypes = [
    { value: '', label: 'Select facility type' },
    { value: 'power-generation', label: 'Power Generation' },
    { value: 'oil-gas', label: 'Oil & Gas Operation' },
    { value: 'military', label: 'Military Installation' },
    { value: 'seafood', label: 'Seafood Processing' },
    { value: 'mining', label: 'Mining Operation' },
    { value: 'manufacturing', label: 'Manufacturing Facility' },
    { value: 'remediation', label: 'Remediation Site' },
    { value: 'other', label: 'Other Industrial Operation' }
  ];

  const projectStages = [
    { value: '', label: 'Select project stage' },
    { value: 'concept', label: 'Concept/Initial Planning' },
    { value: 'feasibility', label: 'Feasibility Study' },
    { value: 'preliminary-design', label: 'Preliminary Design' },
    { value: 'detailed-design', label: 'Detailed Design' },
    { value: 'ready-to-permit', label: 'Ready to Apply for Permits' }
  ];

  const budgetRanges = [
    { value: '', label: 'Select budget range' },
    { value: 'under-100k', label: 'Under $100,000' },
    { value: '100k-500k', label: '$100,000 - $500,000' },
    { value: '500k-1m', label: '$500,000 - $1 Million' },
    { value: '1m-5m', label: '$1 Million - $5 Million' },
    { value: 'over-5m', label: 'Over $5 Million' },
    { value: 'tbd', label: 'To Be Determined' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/permit-feasibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your feasibility assessment request has been submitted successfully. We will contact you shortly.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          projectType: '', facilityType: '', projectLocation: '', projectStage: '',
          estimatedCapacity: '', fuelTypes: '', constructionTimeline: '', budgetRange: '',
          specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again or contact us directly.');
    }
  };

  const assessmentComponents = [
    {
      title: 'Regulatory Review',
      description: 'Comprehensive analysis of federal, state, and local regulations applicable to your project.',
      items: ['Clean Air Act requirements', 'Alaska state regulations', 'Local ordinances', 'Industry-specific standards'],
      icon: Scale
    },
    {
      title: 'Permit Identification',
      description: 'Determination of all required permits and authorizations for your operation.',
      items: ['Air quality permits', 'Water discharge permits', 'Hazardous waste permits', 'Special use permits'],
      icon: FileStack
    },
    {
      title: 'Timeline Analysis',
      description: 'Realistic timeline estimates for each permit and overall project schedule.',
      items: ['Application preparation time', 'Agency review periods', 'Public comment periods', 'Appeal windows'],
      icon: Hourglass
    },
    {
      title: 'Cost Estimation',
      description: 'Projected costs for permitting process and compliance requirements.',
      items: ['Application fees', 'Consulting costs', 'Monitoring equipment', 'Ongoing compliance costs'],
      icon: DollarSign
    },
    {
      title: 'Risk Assessment',
      description: 'Identification of potential regulatory obstacles and mitigation strategies.',
      items: ['Emission threshold concerns', 'Environmental impacts', 'Community opposition', 'Technical challenges'],
      icon: ShieldX
    },
    {
      title: 'Alternative Analysis',
      description: 'Evaluation of alternative approaches to minimize regulatory burden.',
      items: ['Emission reduction options', 'Technology alternatives', 'Operational modifications', 'Phasing strategies'],
      icon: GitFork
    }
  ];

  const benefits = [
    'Avoid costly project delays by identifying permit requirements early',
    'Make informed decisions about project design and location',
    'Understand realistic timelines before committing resources',
    'Identify potential regulatory obstacles before they become problems',
    'Optimize project design to minimize permitting complexity',
    'Budget accurately for permitting and compliance costs',
    'Evaluate multiple project alternatives efficiently',
    'Improve stakeholder communication with clear regulatory roadmap'
  ];

  return (
    <div className="min-h-screen" data-testid="permit-feasibility-page">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxwbGFubmluZ3xlbnwwfHx8fDE3NjExMDExOTF8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Permit Feasibility & Regulatory Strategy
            </h1>
            <p className="text-xl text-gray-100 mb-8">
              Pre-construction analysis to identify regulatory risks, minimize permitting timelines, and optimize capital costs before you break ground.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact-form"
                className="inline-flex items-center px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg"
              >
                Request Analysis
                <Send className="ml-2" size={20} />
              </a>
              <a
                href="#key-deliverables"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                View Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Conduct a Permit Feasibility Study?</h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Before investing significant resources in project development, it's crucial to understand the environmental permitting requirements and associated challenges. Our permit feasibility analysis provides the clarity you need to make informed decisions and avoid costly surprises.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We evaluate your project against applicable regulations, identify all required permits, assess potential obstacles, and provide realistic timelines and cost estimates. This early-stage analysis can save months of delays and hundreds of thousands of dollars.
            </p>
          </div>
        </div>
      </section>

      {/* Comprehensive Feasibility Assessment */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Comprehensive Feasibility Assessment</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {assessmentComponents.map((component, index) => {
              const IconComponent = component.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md border-t-4 border-blue-700">
                  {/* Jewel Style Icon Container */}
                  <div className="bg-violet-600 w-14 h-14 rounded-lg flex items-center justify-center shadow-lg mb-4">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{component.title}</h3>
                  <p className="text-gray-600 mb-4">{component.description}</p>
                  <ul className="space-y-2">
                    {component.items.map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-violet-600 mr-2 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Deliverables - Violet Theme with Jewel Icons */}
      <section id="key-deliverables" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Key Deliverables
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategic regulatory analysis to position your project for permitting success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Deliverable 1: Potential to Emit (PTE) */}
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-violet-600 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                <Calculator className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Potential to Emit (PTE)</h3>
              <p className="text-gray-600">
                Precise emission calculations to determine Title V vs. Minor Source applicability under 18 AAC 50.
              </p>
            </div>

            {/* Deliverable 2: PSD Avoidance Strategy */}
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-violet-600 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                <ShieldCheck className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">PSD Avoidance Strategy</h3>
              <p className="text-gray-600">
                Strategic equipment sizing to avoid complex Prevention of Significant Deterioration (PSD) reviews.
              </p>
            </div>

            {/* Deliverable 3: Fatal Flaw Analysis */}
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-violet-600 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                <AlertTriangle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fatal Flaw Analysis</h3>
              <p className="text-gray-600">
                Identification of 'showstopper' regulations (e.g., Class I visibility impacts, Non-attainment zones) early in the design phase.
              </p>
            </div>

            {/* Deliverable 4: Synthetic Minor Pathways */}
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-violet-600 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Synthetic Minor Pathways</h3>
              <p className="text-gray-600">
                Developing Owner Requested Limits (ORLs) to cap emissions and simplify ongoing compliance obligations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits of Early Feasibility Analysis */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Benefits of Early Feasibility Analysis</h2>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start bg-blue-50 p-4 rounded-lg">
                <CheckCircle className="w-5 h-5 text-violet-600 mr-3 flex-shrink-0 mt-1" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feasibility Study Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Feasibility Study Process</h2>
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-700">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold text-lg">1</div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Initial Consultation</h3>
                  <p className="text-gray-600 mb-2">Discuss project goals, scope, location, and preliminary design concepts.</p>
                  <p className="text-sm text-blue-700 font-semibold">Duration: 1-2 days</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-700">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold text-lg">2</div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Regulatory Research</h3>
                  <p className="text-gray-600 mb-2">Thorough review of all applicable federal, state, and local regulations.</p>
                  <p className="text-sm text-blue-700 font-semibold">Duration: 1-2 weeks</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-700">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold text-lg">3</div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Analysis & Evaluation</h3>
                  <p className="text-gray-600 mb-2">Detailed assessment of permit requirements, timelines, costs, and risks.</p>
                  <p className="text-sm text-blue-700 font-semibold">Duration: 1-2 weeks</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-700">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold text-lg">4</div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Report Preparation</h3>
                  <p className="text-gray-600 mb-2">Comprehensive written report with findings, recommendations, and action plan.</p>
                  <p className="text-sm text-blue-700 font-semibold">Duration: 3-5 days</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-700">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold text-lg">5</div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Results Presentation</h3>
                  <p className="text-gray-600 mb-2">Detailed presentation of findings with opportunity for questions and discussion.</p>
                  <p className="text-sm text-blue-700 font-semibold">Duration: 1-2 hours</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form - Violet Theme */}
      <section id="contact-form" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-violet-700 text-white p-8 rounded-t-xl">
              <div className="flex items-center mb-4">
                <Send className="w-10 h-10 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold">Request Regulatory Analysis</h2>
                  <p className="text-violet-100 mt-2">Tell us about your project for a customized feasibility assessment</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl border border-gray-200">
              {/* Contact Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company/Organization *</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Project Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Project Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Type</label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    >
                      {projectTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Facility Type</label>
                    <select
                      name="facilityType"
                      value={formData.facilityType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    >
                      {facilityTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Location</label>
                    <input
                      type="text"
                      name="projectLocation"
                      value={formData.projectLocation}
                      onChange={handleChange}
                      placeholder="City/Region in Alaska"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Stage</label>
                    <select
                      name="projectStage"
                      value={formData.projectStage}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    >
                      {projectStages.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Capacity/Scale</label>
                    <input
                      type="text"
                      name="estimatedCapacity"
                      value={formData.estimatedCapacity}
                      onChange={handleChange}
                      placeholder="e.g., 5 MW, 100 barrels/day"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
                    <select
                      name="budgetRange"
                      value={formData.budgetRange}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    >
                      {budgetRanges.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Types or Process Description</label>
                  <textarea
                    name="fuelTypes"
                    value={formData.fuelTypes}
                    onChange={handleChange}
                    rows="2"
                    placeholder="e.g., Natural gas, diesel, or describe main processes"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Desired Construction Timeline</label>
                  <input
                    type="text"
                    name="constructionTimeline"
                    value={formData.constructionTimeline}
                    onChange={handleChange}
                    placeholder="e.g., Start Q2 2026"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Additional Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Additional Details</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Requirements or Concerns <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="specificRequirements"
                    value={formData.specificRequirements}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="e.g., Permit feasibility concerns, emission thresholds, timeline constraints, environmental sensitivities, nearby receptors, PSD applicability, air quality impacts, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Any other details about your project, specific questions, or concerns"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-center">
                <button
                  type="submit"
                  className="inline-flex items-center px-8 py-4 bg-violet-700 text-white rounded-lg font-semibold hover:bg-violet-800 transition-colors shadow-lg"
                >
                  Submit Regulatory Analysis Request
                  <Send className="ml-2" size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Plan Your Project with Regulatory Certainty
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Identify permitting pathways and avoid costly regulatory surprises before you invest
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
          >
            Schedule Consultation
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PermitFeasibility;